import { AnimatePresence, motion, Point } from 'framer-motion'
import isEqual from 'lodash/isEqual'
import uniqueId from 'lodash/uniqueId'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Icon } from '../Icon'
import { CSSBackgroundSize } from '../types/css'
import { BaseProps } from '../types/props'
import {
  CloseButton,
  Counter,
  DownloadButton,
  ImageDescription,
  NavArrows,
  Wrapper,
} from './components'
import { swipePower, SWIPE_CONFIDENCE_THRESHOLD, VARIANTS, wrap } from './util'

export interface Image {
  id?: string | number
  file: string | File
  name?: string
  description?: string
  imageSize?: CSSBackgroundSize
  imageBackgroundColor?: string
}
interface LoadedImage {
  id: string | number
  url: string
  name?: string
  description?: string
  imageSize: CSSBackgroundSize
  imageBackgroundColor: string
}

export interface ImageViewerRef {
  scrollViewer: (delta: number) => void
}

export interface ImageViewerProps extends BaseProps {
  images: Image[]

  /** Use as a popup */
  popup?: boolean
  /** Whether popup is open. Only relevant when popup === true */
  open?: boolean
  /** Whether to fade name/description on mouseleave */
  fadeInformation?: boolean
  /** Whether to show navigation arrows */
  showArrows?: boolean
  /** Whether to use arrow left and right to navigate */
  noArrowKeyNavigation?: boolean
  /** Whether to display image counter */
  showImageCounter?: boolean
  /** Whether to fade image counter on mouseleave */
  fadeImageCounter?: boolean
  /** Whether to show a download button */
  showDownloadButton?: boolean
  /** Disable close on escape button press. Only relevant when popup === true */
  noCloseOnEscape?: boolean
  /**
   * Turn of shadow behind images
   * @deprecated This no longer has an effect
   **/
  noShadow?: boolean

  /** Id of image to show initially */
  initialImage?: string | number
  tabIndex?: number
  background?: string
  imageSize?: CSSBackgroundSize
  imageBackgroundColor?: string

  width?: string
  height?: string
  zIndex?: number
  /** @deprecated This no longer has an effect */
  padding?: string

  infoWidth?: string
  infoMarginBottom?: string
  infoColor?: string
  infoBackground?: string

  onImageScroll?: (image: LoadedImage) => void
  onImageClick?: (image: LoadedImage) => void
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void
  onClose?: () => void
}

export const ImageViewer = React.forwardRef<ImageViewerRef, ImageViewerProps>(
  (
    {
      className,
      id,

      images,

      popup = false,
      open,
      fadeInformation = false,
      showArrows = false,
      noArrowKeyNavigation = false,
      showImageCounter = false,
      fadeImageCounter = false,
      showDownloadButton = false,
      noCloseOnEscape = false,

      initialImage,
      tabIndex = 0,
      background,
      imageSize = 'cover',
      imageBackgroundColor = 'inherit',

      width = '800px',
      height = '600px',
      zIndex = 1,
      infoWidth,
      infoMarginBottom,
      infoColor = 'white',
      infoBackground = 'rgba(0, 0, 0, 0.5)',

      onImageScroll,
      onImageClick,
      onMouseEnter,
      onMouseLeave,
      /** Fired when closing of popup is requested */
      onClose,
    },
    ref
  ) => {
    if (popup) {
      if (typeof open === 'undefined')
        throw new Error(
          'ImageViewer: If prop "popup" is true, prop "open" must be defined.'
        )

      width = `${window.innerWidth}px`
      height = `${window.innerHeight}px`
    }

    useImperativeHandle(ref, () => ({
      scrollViewer,
    }))

    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const prevLoadedImages = useRef<LoadedImage[] | null>(null)
    const prevPage = useRef(0)

    const [loadedImages, setLoadedImages] = useState<LoadedImage[]>([])
    const [mouseIsOver, setMouseIsOver] = useState(false)

    const [[page, direction], setPage] = useState([0, 0])

    const imageIndex = useMemo(
      () => wrap(0, loadedImages.length, page),
      [loadedImages.length, page]
    )
    const image = useMemo<LoadedImage | null>(
      () => loadedImages[imageIndex] ?? null,
      [images, imageIndex]
    )

    /* Load images */

    useEffect(() => {
      type LoadedImageWithIndex = LoadedImage & { index: number }
      const fileReaderPromises: Promise<LoadedImageWithIndex>[] = []
      const finishedImages: LoadedImageWithIndex[] = []

      const createImage = (image: Image, url: string, index: number) => ({
        id: image.id ?? uniqueId('image'),
        url: url,
        name: image.name,
        description: image.description,
        imageSize: image.imageSize ?? imageSize,
        imageBackgroundColor:
          image.imageBackgroundColor ?? imageBackgroundColor,
        index,
      })

      for (let index = 0; index < images.length; index++) {
        const image = images[index]
        if (typeof image.file === 'string') {
          finishedImages.push(createImage(image, image.file, index))
        } else {
          fileReaderPromises.push(
            new Promise((resolve, reject) => {
              if (typeof image.file === 'string') {
                // This seems unnecessary, but unfortunately TypeScript
                // doesn't realize that the typecheck is already done
                // from inside the promise
                reject()
                return
              }

              const fr = new FileReader()
              fr.onload = e => {
                const result = e.target?.result?.toString()
                if (!result) reject()
                else resolve(createImage(image, result, index))
              }
              fr.onerror = reject
              fr.readAsDataURL(image.file)
            })
          )
        }
      }

      Promise.all(fileReaderPromises).then(imgs => {
        const allImages = [
          ...finishedImages,
          ...imgs.filter(i => typeof i.url === 'string'),
        ]
        setLoadedImages(allImages.sort((a, b) => a.index - b.index))
      })
    }, [imageBackgroundColor, imageSize, images])

    /* Methods */

    const scrollViewer = useCallback((delta: number) => {
      setPage(([page]) => [page + delta, delta < 0 ? -1 : 1])
    }, [])

    /* Event handlers */

    const handleMouseEnter = useCallback(
      (evt: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(evt)
        if (evt.defaultPrevented) return

        setMouseIsOver(true)
      },
      [onMouseEnter]
    )
    const handleMouseLeave = useCallback(
      (evt: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(evt)
        if (evt.defaultPrevented) return

        setMouseIsOver(false)
      },
      [onMouseLeave]
    )
    const handleDragEnd = useCallback(
      (offset: Point, velocity: Point) => {
        const swipe = swipePower(offset.x, velocity.x)

        if (swipe < -SWIPE_CONFIDENCE_THRESHOLD) scrollViewer(1)
        else if (swipe > SWIPE_CONFIDENCE_THRESHOLD) scrollViewer(-1)
      },
      [scrollViewer]
    )

    const handleDownload = useCallback(async () => {
      if (!image) return

      const img = await window.fetch(image.url)
      const blob = await img.blob()
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = image.name ?? 'image'
      a.click()
    }, [image])

    useEffect(() => {
      if (!image || page === prevPage.current) return
      prevPage.current = page
      onImageScroll?.(image)
    }, [page, onImageScroll, image])

    useEffect(() => {
      if (typeof initialImage === 'undefined') return
      if (
        popup &&
        !open &&
        prevLoadedImages.current !== null &&
        isEqual(prevLoadedImages.current, loadedImages)
      )
        return
      prevLoadedImages.current = loadedImages

      const imageIdx = loadedImages.findIndex(img => img.id === initialImage)
      if (imageIdx < 0) return

      setPage([imageIdx, 0])
    }, [initialImage, loadedImages, popup, open, scrollViewer])

    useEffect(() => {
      const handler = ({ key }: KeyboardEvent) => {
        switch (key) {
          case 'Escape':
            if (!noCloseOnEscape && popup && open) onClose?.()
            break
          case 'ArrowLeft':
            if (!noArrowKeyNavigation) scrollViewer(-1)
            break
          case 'ArrowRight':
            if (!noArrowKeyNavigation) scrollViewer(1)
            break
        }
      }

      document.addEventListener('keydown', handler)

      return () => {
        document.removeEventListener('keydown', handler)
      }
    }, [
      noCloseOnEscape,
      popup,
      open,
      noArrowKeyNavigation,
      onClose,
      scrollViewer,
    ])

    if (!image) return null

    return (
      <Wrapper
        ref={wrapperRef}
        className={className}
        id={id}
        tabIndex={tabIndex}
        popup={popup}
        open={open}
        width={width}
        height={height}
        background={background}
        imageSize={image.imageSize}
        imageBackgroundColor={image.imageBackgroundColor}
        zIndex={zIndex}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showArrows && (
          <NavArrows
            className="--image-viewer-nav-arrows"
            leftVisible={mouseIsOver}
            rightVisible={mouseIsOver}
          >
            <div
              className="--image-viewer-nav-arrows-left"
              onClick={() => scrollViewer(-1)}
            >
              <Icon icon="chevron-left" />
            </div>

            <div
              className="--image-viewer-nav-arrows-right"
              onClick={() => scrollViewer(1)}
            >
              <Icon icon="chevron-right" />
            </div>
          </NavArrows>
        )}

        <AnimatePresence initial={false} custom={direction}>
          <motion.img
            key={page}
            src={image.url}
            custom={direction}
            variants={VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(_, { offset, velocity }) =>
              handleDragEnd(offset, velocity)
            }
            onClick={() => onImageClick?.(image)}
          />
        </AnimatePresence>

        {showImageCounter && (
          <Counter
            className="--image-viewer-counter"
            opacity={!fadeImageCounter ? 1 : mouseIsOver ? 1 : 0}
          >
            {(page % loadedImages.length) + 1}&nbsp;<span>/</span>&nbsp;
            {loadedImages.length}
          </Counter>
        )}

        {(image.name || image.description) && (
          <ImageDescription
            className="--image-viewer-description"
            opacity={!fadeInformation ? 1 : mouseIsOver ? 1 : 0}
            width={infoWidth}
            marginBottom={infoMarginBottom}
            color={infoColor}
            background={infoBackground}
          >
            <span className="name">{image.name}</span>
            <p>{image.description}</p>
          </ImageDescription>
        )}

        {showDownloadButton && (
          <DownloadButton className="--image-viewer-download-button">
            <Icon
              icon="download"
              type="solid"
              size="1.2rem"
              color="gray4"
              hoverColor="white"
              cursor="pointer"
              onClick={handleDownload}
            />
          </DownloadButton>
        )}

        {popup && (
          <CloseButton
            className="--image-viewer-close-button"
            onClick={() => onClose?.()}
          >
            <Icon
              icon="times"
              type="light"
              color="#dadada"
              hoverColor="white"
              cursor="pointer"
              size="32px"
            />
          </CloseButton>
        )}
      </Wrapper>
    )
  }
)
